import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_IMAGE } from "@/data/links";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.35 + i * 0.14,
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: d },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax: image drifts slower than foreground.
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="vignette relative h-[100svh] w-full overflow-hidden"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 -top-[10%] h-[120%] w-full will-change-transform"
      >
        <img
          src={HERO_IMAGE}
          alt="Medieval Confessions — a hooded figure writing a confession by candlelight"
          className="h-full w-full object-cover object-center"
          fetchPriority="high"
        />
      </motion.div>

      {/* Cinematic gradient masks */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/30 to-[#050505]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />

      {/* Foreground content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
      >
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.15}
          className="font-accent mb-6 text-[0.62rem] uppercase tracking-[0.5em] text-[#c9a227] sm:text-xs sm:tracking-[0.6em]"
          data-testid="hero-overline"
        >
          Dark Fantasy · Character Songs
        </motion.p>

        <h1
          className="font-serif-display leading-[0.86] text-[#e8e3d2]"
          data-testid="artist-name"
        >
          <span className="block overflow-hidden">
            <motion.span
              variants={line}
              initial="hidden"
              animate="visible"
              custom={0}
              className="block text-[3.4rem] font-500 tracking-tight sm:text-[6rem] lg:text-[8.5rem]"
            >
              Medieval
            </motion.span>
          </span>
          <span className="mt-[-0.12em] block overflow-hidden">
            <motion.span
              variants={line}
              initial="hidden"
              animate="visible"
              custom={1}
              className="block bg-gradient-to-b from-[#f3edda] to-[#b8a679] bg-clip-text text-[3.4rem] italic text-transparent sm:text-[6rem] lg:text-[8.5rem]"
            >
              Confessions
            </motion.span>
          </span>
        </h1>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1.4}
          className="mt-8 flex items-center gap-4"
        >
          <span className="h-px w-10 bg-[#c9a227]/50" />
          <span className="font-accent text-[0.6rem] uppercase tracking-[0.35em] text-[#a39e8f] sm:text-[0.7rem]">
            Stream Everywhere
          </span>
          <span className="h-px w-10 bg-[#c9a227]/50" />
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={1.8}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-accent text-[0.55rem] uppercase tracking-[0.4em] text-[#a39e8f]">
          Descend
        </span>
        <span className="relative block h-10 w-px bg-white/10">
          <span className="scroll-line absolute inset-0 block bg-[#c9a227]" />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
